import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { Alert, Button, Skeleton, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { logHeardError } from '@/utils/errorHelpers'
import { STRIPE_APPEARANCE } from './appearance'
import { getStripe } from './stripeClient'
import { errorCopyFor, type PaymentMethodFlow } from './paymentMethodCopy'
import type { PaymentMethodUpdate } from './usePaymentMethodUpdate'
import type {
  BillingAddress,
  CardSummary,
  PaymentMethodUpdateError,
  SetupIntentSession,
} from './payment-method.types'

interface PaymentMethodFormProps {
  update: PaymentMethodUpdate
  flow: PaymentMethodFlow
  currentCard: CardSummary | null
  onCancel: () => void
  footer?: ReactNode
  /** The failure that opened this form was itself a decline, so a new decline reads "too". */
  priorDecline?: boolean
  focusOnMount?: boolean
}

interface ElementsFormProps extends Omit<
  PaymentMethodFormProps,
  'onCancel' | 'focusOnMount'
> {
  session: SetupIntentSession
  onRetry: () => void
  onSettled: (clientSecret: string) => void
  saveSlot: HTMLDivElement | null
}

const LOADING_MESSAGE = 'Loading secure form…'

// React 18 has no typed `inert` prop and warns on `inert={true}`.
const inertWhen = (on: boolean): { inert?: string } => (on ? { inert: '' } : {})

export const addressDefaults = (address: BillingAddress | null) =>
  address
    ? {
        name: address.name ?? undefined,
        address: {
          line1: address.line1 ?? undefined,
          line2: address.line2 ?? undefined,
          city: address.city ?? undefined,
          state: address.state ?? undefined,
          postal_code: address.postalCode ?? undefined,
          country: address.country ?? 'US',
        },
      }
    : undefined

const FormSkeleton = (): JSX.Element => (
  <div className="flex flex-col gap-4" aria-hidden>
    {['card', 'expiry', 'name', 'line1', 'line2', 'city'].map((row) => (
      <Skeleton key={row} className="h-10 w-full" />
    ))}
  </div>
)

const ErrorAlert = ({
  flow,
  error,
  card,
  priorDecline,
}: {
  flow: PaymentMethodFlow
  error: PaymentMethodUpdateError
  card: CardSummary | null
  priorDecline?: boolean
}): JSX.Element => {
  const copy = errorCopyFor({ flow, error, card, priorDecline })
  return (
    <Alert variant="error" title={copy.title}>
      {copy.description}
    </Alert>
  )
}

const RetryButton = ({ onRetry }: { onRetry: () => void }): JSX.Element => (
  <Button variant="secondary" onClick={onRetry}>
    Try again
  </Button>
)

const SaveButton = ({
  update,
}: {
  update: PaymentMethodUpdate
}): JSX.Element => {
  const stripe = useStripe()
  const elements = useElements()
  const saving = update.status === 'saving'
  return (
    <Button
      disabled={
        saving || !stripe || !elements || update.error === 'finalize_failed'
      }
      onClick={() => {
        if (stripe && elements) void update.save({ stripe, elements })
      }}
    >
      {saving ? 'Working on it…' : 'Save card'}
    </Button>
  )
}

const ElementsForm = ({
  update,
  session,
  flow,
  currentCard,
  onRetry,
  onSettled,
  footer,
  priorDecline,
  saveSlot,
}: ElementsFormProps): JSX.Element => {
  const [paymentReady, setPaymentReady] = useState(false)
  const [addressReady, setAddressReady] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const ready = paymentReady && addressReady && !loadFailed
  const settled = ready || loadFailed
  const readOnly = update.status === 'saving'
  const error = loadFailed ? 'load_failed' : update.error

  const handleLoadError = (event: { error: unknown }): void => {
    logHeardError(event.error)
    setLoadFailed(true)
  }

  useEffect(() => {
    if (settled) onSettled(session.clientSecret)
  }, [settled, onSettled, session.clientSecret])

  return (
    <Elements
      stripe={getStripe(session.publishableKey)}
      options={{
        clientSecret: session.clientSecret,
        appearance: STRIPE_APPEARANCE,
      }}
    >
      {error && (
        <ErrorAlert
          flow={flow}
          error={error}
          card={currentCard}
          priorDecline={priorDecline}
        />
      )}
      {loadFailed && <RetryButton onRetry={onRetry} />}
      <div className="relative flex flex-col gap-4">
        {!settled && (
          <div
            className="absolute inset-0 flex flex-col gap-4 bg-white"
            aria-hidden
          >
            <FormSkeleton />
            <Text
              variant={TypographyStyles.BODY_SM}
              className="text-neutral-500"
            >
              {LOADING_MESSAGE}
            </Text>
          </div>
        )}
        <div
          className={
            ready ? 'flex flex-col gap-4' : 'invisible flex flex-col gap-4'
          }
          aria-hidden={ready ? undefined : true}
          {...inertWhen(!ready)}
        >
          <PaymentElement
            onReady={() => setPaymentReady(true)}
            onLoadError={handleLoadError}
            options={{
              layout: 'tabs',
              wallets: {
                applePay: 'never',
                googlePay: 'never',
                link: 'never',
              },
              readOnly,
            }}
          />
          <div
            className={readOnly ? 'opacity-50' : undefined}
            {...inertWhen(readOnly)}
          >
            <AddressElement
              onReady={() => setAddressReady(true)}
              onLoadError={handleLoadError}
              options={{
                mode: 'billing',
                defaultValues: addressDefaults(session.billingAddress),
              }}
            />
          </div>
        </div>
      </div>
      {footer}
      {ready &&
        saveSlot &&
        createPortal(<SaveButton update={update} />, saveSlot)}
    </Elements>
  )
}

export const PaymentMethodForm = ({
  update,
  flow,
  currentCard,
  onCancel,
  footer,
  priorDecline,
  focusOnMount = false,
}: PaymentMethodFormProps): JSX.Element | null => {
  const { session, error, status } = update
  const containerRef = useRef<HTMLDivElement>(null)
  const focusOnSessionRef = useRef(false)
  const [settledSecret, setSettledSecret] = useState<string | null>(null)
  const [saveSlot, setSaveSlot] = useState<HTMLDivElement | null>(null)
  const loading = session
    ? settledSecret !== session.clientSecret
    : status === 'loading'

  useEffect(() => {
    if (focusOnMount) containerRef.current?.focus()
  }, [focusOnMount])

  useEffect(() => {
    if (!session || !focusOnSessionRef.current) return
    focusOnSessionRef.current = false
    containerRef.current?.focus()
  }, [session])

  const retry = (): void => {
    // Try again unmounts while the new session loads, which would drop focus to <body>.
    focusOnSessionRef.current = true
    update.cancel()
    update.open()
  }

  if (status === 'idle' || status === 'saved') return null

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className="flex flex-col gap-4 outline-none"
    >
      <p role="status" className="sr-only">
        {loading ? LOADING_MESSAGE : ''}
      </p>
      {session ? (
        <ElementsForm
          key={session.clientSecret}
          update={update}
          session={session}
          flow={flow}
          currentCard={currentCard}
          onRetry={retry}
          onSettled={setSettledSecret}
          footer={footer}
          priorDecline={priorDecline}
          saveSlot={saveSlot}
        />
      ) : (
        <>
          {error && (
            <ErrorAlert
              flow={flow}
              error={error}
              card={currentCard}
              priorDecline={priorDecline}
            />
          )}
          {error ? <RetryButton onRetry={retry} /> : <FormSkeleton />}
        </>
      )}
      {/* Save portals in from inside Elements, so this one Cancel node keeps focus across session and ready changes. */}
      <div className="flex gap-3">
        <div ref={setSaveSlot} className="contents" />
        <Button
          variant="secondary"
          disabled={status === 'saving'}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
